import type {
  AccountUser,
  AdminAccount,
  GeocodeResult,
  HomeLocation,
  MeResponse,
  NotificationsResponse,
  NotifySettings,
  SavedView,
  Sighting,
  SpeciesCode,
  Track,
  ViewingLocation,
  ViewpointSubmission,
} from '@whaletail/shared';

export interface Health {
  sightingCount: number;
  trackCount: number;
  newestSightingAt: number | null;
  lastPollAt: number | null;
  lastPollOk: boolean;
  lastError: string | null;
  retentionHours: number;
  pollIntervalMinutes: number;
  staleWarningMinutes: number;
  stale: boolean;
  ok: boolean;
}

/** An API failure carrying the server's own, human-readable message. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly field?: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: { accept: 'application/json', ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection.', 0);
  }
  if (!res.ok) {
    let message = `Request failed (${res.status}).`;
    let field: string | undefined;
    try {
      const j = (await res.json()) as { error?: string; field?: string };
      if (j.error) message = j.error;
      field = j.field;
    } catch {
      /* non-JSON error body; keep the generic message */
    }
    throw new ApiError(message, res.status, field);
  }
  return (await res.json()) as T;
}

const get = <T>(path: string) => request<T>('GET', path);

// --- data ---------------------------------------------------------------

export const fetchTracks = (hours: number) => get<Track[]>(`/api/tracks?hours=${hours}`);
export const fetchHealth = () => get<Health>('/api/health');
export const fetchViewingLocations = () => get<ViewingLocation[]>('/api/viewing-locations');

// --- account ------------------------------------------------------------

export const getMe = () => get<MeResponse>('/api/me');
export const registerAccount = (username: string, password: string) =>
  request<MeResponse>('POST', '/api/auth/register', { username, password });
export const signIn = (username: string, password: string) =>
  request<MeResponse>('POST', '/api/auth/login', { username, password });
export const signOut = () => request<{ ok: true }>('POST', '/api/auth/logout', {});
export const setHome = (home: HomeLocation) =>
  request<{ user: AccountUser }>('PUT', '/api/account/home', home);
export const clearHome = () => request<{ user: AccountUser }>('DELETE', '/api/account/home');
export const changePassword = (current: string, next: string) =>
  request<{ ok: true }>('POST', '/api/account/password', { current, next });
export const deleteAccount = (password: string) =>
  request<{ ok: true }>('DELETE', '/api/account', { password });

// --- nearby-whale alerts --------------------------------------------------

export const getNotifications = () => get<NotificationsResponse>('/api/notifications');
export const saveNotifySettings = (s: NotifySettings) =>
  request<NotificationsResponse>('PUT', '/api/notifications/settings', s);
export const subscribePush = (sub: { endpoint: string; keys: { p256dh: string; auth: string }; label?: string }) =>
  request<NotificationsResponse>('POST', '/api/notifications/subscriptions', sub);
export const unsubscribePush = (endpoint: string) =>
  request<NotificationsResponse>('DELETE', '/api/notifications/subscriptions', { endpoint });
export const testPush = () =>
  request<{ sent: number; failed: number; removed: number; errors: string[]; deviceCount: number }>(
    'POST',
    '/api/notifications/test',
    {},
  );

// --- saved views --------------------------------------------------------

export type NewSavedView = Omit<SavedView, 'id' | 'createdAt' | 'isDefault'>;
/** The camera and filters at the moment of saving — everything but the name. */
export type ViewSnapshot = Omit<NewSavedView, 'name'>;
export const listViews = () => get<SavedView[]>('/api/views');
export const createView = (v: NewSavedView) => request<SavedView>('POST', '/api/views', v);
export const renameView = (id: number, name: string) => request<SavedView[]>('PATCH', `/api/views/${id}`, { name });
export const setDefaultView = (id: number | null) => request<SavedView[]>('PUT', '/api/views/default', { id });
export const deleteView = (id: number) => request<SavedView[]>('DELETE', `/api/views/${id}`);

// --- view points --------------------------------------------------------

export interface NewViewpoint {
  name: string;
  lat: number;
  lon: number;
  region?: string | null;
  note?: string | null;
  hike?: boolean;
}
export const submitViewpoint = (v: NewViewpoint) =>
  request<{ viewpoint: ViewpointSubmission; autoApproved: boolean }>('POST', '/api/viewpoints', v);
export const myViewpoints = () => get<ViewpointSubmission[]>('/api/viewpoints/mine');
export const removeViewpoint = (id: number) => request<{ ok: true }>('DELETE', `/api/viewpoints/${id}`);

export interface AdminQueue {
  pending: ViewpointSubmission[];
  approved: ViewpointSubmission[];
  pendingCount: number;
}
export const adminQueue = () => get<AdminQueue>('/api/admin/viewpoints');
export const approveViewpoint = (
  id: number,
  edit: { name?: string; region?: string | null; note?: string | null; hike?: boolean } = {},
) => request<ViewpointSubmission>('POST', `/api/admin/viewpoints/${id}/approve`, edit);
export const rejectViewpoint = (id: number, reason?: string) =>
  request<ViewpointSubmission>('POST', `/api/admin/viewpoints/${id}/reject`, { reason: reason || null });

// This server has no public sign-up (see AccountUser.role/registrationOpen in
// MeResponse) — an admin creates every account by hand instead.
export const adminListAccounts = () => get<AdminAccount[]>('/api/admin/accounts');
export const adminCreateAccount = (username: string, password: string, role?: 'user' | 'admin') =>
  request<AdminAccount>('POST', '/api/admin/accounts', { username, password, ...(role ? { role } : {}) });
export const adminSetPassword = (id: number, password: string) =>
  request<AdminAccount>('POST', `/api/admin/accounts/${id}/password`, { password });
export const adminSetRole = (id: number, role: 'user' | 'admin') =>
  request<AdminAccount>('POST', `/api/admin/accounts/${id}/role`, { role });
export const adminSetPasswordExpired = (id: number, expired: boolean) =>
  request<AdminAccount>('POST', `/api/admin/accounts/${id}/expire`, { expired });

// --- server updates -------------------------------------------------------
// The server downloads and stages a GitHub release; the supervisor that
// start-prod.bat runs applies it and restarts the server in the same window —
// see .github/workflows/release.yml, scripts/supervisor.ts, scripts/self-update.ts.

export interface ReleaseCheck {
  currentVersion: string;
  latestVersion: string | null;
  updateAvailable: boolean;
  publishedAt: string | null;
  releaseUrl: string | null;
  error: string | null;
}
export interface UpdateProgress {
  state: string;
  version?: string;
  updatedAt?: number;
  error?: string;
}
export interface UnplacedSms {
  id: number;
  receivedAt: number;
  rawText: string;
  /** The individual reports in the text that named no place the gazetteer knows. */
  unplacedReports: string[];
}
export const adminUnplacedSms = () => get<UnplacedSms[]>('/api/admin/sms/unplaced');
export interface SmsRerunResult {
  ok: boolean;
  reports: number;
  plotted: number;
  unplaced: number;
  deduped: number;
  /** Nothing left unplaced: the text was marked trained and leaves the list. */
  trained: boolean;
  error?: string;
}
export const adminRerunSms = (id: number) => request<SmsRerunResult>('POST', `/api/admin/sms/${id}/rerun`, {});
export const adminMarkSmsTrained = (id: number) => request<{ ok: true }>('POST', `/api/admin/sms/${id}/trained`, {});

export const adminCheckForUpdate = () => get<ReleaseCheck>('/api/admin/update/check');
export const adminStartUpdate = () => request<{ ok: true; version: string }>('POST', '/api/admin/update/start', {});
export const adminUpdateProgress = () => get<UpdateProgress>('/api/admin/update/progress');

export const geocodeAddress = (q: string) => get<GeocodeResult[]>(`/api/geocode?q=${encodeURIComponent(q)}`);

// --- display ------------------------------------------------------------

/**
 * Display config per species. Color is never part of this — see trackColor
 * below for how a point's map/log color is actually chosen. `shape` is the
 * one remaining visual species encoding on the map (live markers); text
 * (this `label`/`short`) carries species everywhere else (report log, hover
 * popup).
 */
export type Shape = 'circle' | 'diamond' | 'square' | 'triangle';

export interface SpeciesDisplay {
  code: SpeciesCode;
  label: string;
  short: string;
  shape: Shape;
}

export const SPECIES_DISPLAY: SpeciesDisplay[] = [
  { code: 'srkw', label: 'Southern Resident orca', short: 'SRKW', shape: 'circle' },
  { code: 'biggs', label: "Bigg's (transient) orca", short: "Bigg's", shape: 'circle' },
  { code: 'orca', label: 'Orca — ecotype unconfirmed', short: 'Orca', shape: 'circle' },
  { code: 'humpback', label: 'Humpback', short: 'Humpback', shape: 'diamond' },
  { code: 'gray', label: 'Gray whale', short: 'Gray', shape: 'square' },
  { code: 'minke', label: 'Minke whale', short: 'Minke', shape: 'triangle' },
  { code: 'porpoise', label: 'Porpoise', short: 'Porpoise', shape: 'triangle' },
  { code: 'other', label: 'Unspecified cetacean', short: 'Unspecified', shape: 'square' },
];

export const displayFor = (code: SpeciesCode): SpeciesDisplay =>
  SPECIES_DISPLAY.find((s) => s.code === code) ?? SPECIES_DISPLAY[SPECIES_DISPLAY.length - 1]!;

export function resolveColor(cssVar: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  return v || '#6b6f74';
}

/**
 * Map color is assigned per *track*, not per species. Two reasons: a species
 * legend can only give a handful of hues an all-pairs colorblind-safe
 * separation (see theme.css's doc comment — only three hues clear that bar),
 * so every simultaneous Bigg's pod used to share one color and read as a
 * single tangle when their trails crossed; and once color no longer means
 * "which species," a legend explaining it is no longer meaningful either — so
 * there isn't one. Species is still shown as text everywhere it matters (the
 * report log, hover details, live-marker labels) and as marker SHAPE, just
 * not as map color.
 *
 * This deliberately does not attempt formal colorblind-safe all-pairs
 * separation the way the species palette did: with up to a few dozen tracks
 * active in a retention window, that many mutually-distinguishable hues does
 * not exist. The goal here is narrower — give each track a color that's
 * usually distinct from its neighbors so a line reads as one continuous
 * trail — not a rigorously validated categorical encoding.
 */
const TRACK_PALETTE_SIZE = 12;

/** A stable small hash — deterministic per track id, so a track's color never changes between renders or reloads (its id itself is stable across polls; see tracks.ts). */
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** The fallback color for a point with no track association (defensive — every marker drawn today always has one, see MapView.tsx). */
export const NO_TRACK_COLOR = { light: '#6b6f74', dark: '#9a9ea3' };

export function trackColor(trackId: string | null | undefined, dark: boolean): string {
  if (!trackId) return dark ? NO_TRACK_COLOR.dark : NO_TRACK_COLOR.light;
  const hue = (hashString(trackId) % TRACK_PALETTE_SIZE) * (360 / TRACK_PALETTE_SIZE);
  const s = dark ? 68 : 62;
  const l = dark ? 60 : 46;
  return `hsl(${hue.toFixed(1)} ${s}% ${l}%)`;
}

/** "23 min ago", "3 h 10 m ago" — compact and unambiguous. */
export function ago(ms: number): string {
  const mins = Math.max(0, Math.round(ms / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `${h} h ${m} m ago` : `${h} h ago`;
  const d = Math.floor(h / 24);
  return `${d} d ${h % 24} h ago`;
}

export const clockTime = (t: number): string =>
  new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export const clockDateTime = (t: number): string =>
  new Date(t).toLocaleString([], {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

/** Flatten tracks to sightings, newest first — the log panel's view of the data. */
export function flattenTracks(tracks: Track[]): Array<Sighting & { trackId: string }> {
  const out: Array<Sighting & { trackId: string }> = [];
  for (const t of tracks) for (const p of t.points) out.push({ ...p, trackId: t.id });
  return out.sort((a, b) => b.observedAt - a.observedAt);
}

// --- dates ----------------------------------------------------------------

const pad2 = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DD` in the viewer's local timezone — the format <input type="date"> uses. */
export const toDateKey = (t: number): string => {
  const d = new Date(t);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/**
 * Local midnight at the start of a `YYYY-MM-DD` day. Built from the parts, not
 * `new Date("2026-09-21")`, which parses a bare date as UTC and would put the
 * start of the day 7-8 hours off for anyone in the Pacific timezone.
 */
export function dayStart(key: string): number {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).getTime();
}

/** The start of the next local day. Not `+ 24h`, which is wrong across a DST change. */
export function dayEnd(key: string): number {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1).getTime();
}

export const shiftDateKey = (key: string, days: number): string => {
  const [y, m, d] = key.split('-').map(Number);
  return toDateKey(new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days).getTime());
};

export const formatDateKey = (key: string): string =>
  new Date(dayStart(key)).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });


/** 120 reads better as "5d" than "120h". */
export const trailLabel = (hours: number): string => (hours >= 48 ? `${Math.round(hours / 24)}d` : `${hours}h`);
